import { MenuItem } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { isNil } from "lodash";
import moment from "moment";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "../../../../components/DataTable/DataTable";
import { IDataTableRow } from "../../../../components/DataTable/DataTableBody/DataTableBody";
import {
  DataTableContextFilters,
  useDataTableContext,
} from "../../../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FilterType } from "../../../../components/DataTable/DataTableFilters/DataTableFilters";
import { Column } from "../../../../components/DataTable/DataTableHead/DataTableHead";
import { FmpFilters } from "../../../../constants/filtering.constants";
import useAppContext from "../../../../context/hooks/useAppContext";
import useFleetManagerContext from "../../../../context/hooks/useFleetManagerContext";
import useMyFleetContext from "../../../../context/hooks/useMyFleetContext";
import { getParkingProductOptions } from "../../../../helpers/selectOptionHelper";
import { parseDVARowKey, stringifyDVARowKey } from "../../../../helpers/vehicleAssignment.utils";
import { ForFleetManager } from "../../../../models/ForFleetManager";
import { ParkingProductType } from "../../../../models/ParkingProductModel";
import {
  DelegatedParkingProductModel,
  VehicleAssignment,
  VehicleAssignmentMyFleet,
  VehicleAssignmentProduct
} from "../../../../models/delegations/DelegatedParkingProductsModel";
import { WithFiltersAndPaging } from "../../../../models/filters/WithFiltersAndPaging";
import { convertFiltersToGetVehicleAssignmentsMyFleetQuery } from "../../../../services/parkingProducts.service";
import { useAppDispatch, useAppSelector } from "../../../../store";
import parkingProductsActions from "../../../../store/actions/parkingProducts.actions";
import * as parkingProductsThunks from "../../../../store/actions/parkingProducts.thunks";
import { ForbiddenIconMenu } from "../../../../ui/Icons/ForbiddenIcon";
import VehicleIcon from "../../../../ui/Icons/Vehicle";
import {
  DeleteDelegeeIcon,
  DeleteDelegeeMenuItem,
  EditIcon,
  NoPaddingGrid,
} from "../../FleetManagerDataTable/StyledComponents";
import { toProductsJsx } from "../../Helpers/ParkingRightsHelpers";
import filteredColumnObjectBuilder from "../../MyProducts/ObjectBuilders/FilteredColumnObjectBuilder";
import { AssignedVehicleRow } from "../../MyProducts/ParkingRightsDataTable/VehiclesTable";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import {
  NumberPlate,
  selectEnableFleetVehicleScheduling,
  selectShowActiveSession,
  selectUseBulkVehicleAssignments
} from "../../MyProducts/ParkingRightsDataTable/common";

export interface VehicleAssignmentRow extends IDataTableRow {
  numberPlate: JSX.Element;
  description?: JSX.Element;
  products: JSX.Element | null;
  assignedDate: string;
}
type VehicleAssignmentsTableProps = Readonly<{
  onAssignVehicleClicked: () => void;
  onInviteFleetMemberClicked: () => void;
  onWhitelistVehiclesClicked: () => void;
  onRevokeClicked: () => void;
  onDeleteClicked: () => void;
  fetchKey: number;
  excludedVehicleAssignmentIds: string[];
  subHeading?: ReactNode;
}>

const toDelegatedParkingProductModel = (products: VehicleAssignmentProduct[]) : DelegatedParkingProductModel[] => 
  products.map(p =>{ return { parkingRightId: p.parkingRightId, location: p.locationName, name:p.pmcName, pmcId: p.pmcId, created: null!, id: null!, status: null!, flexBudgetRemainingTime: null!, flexBudgetUnitType: null!}});

const _createData = (vehicleAssignment: VehicleAssignment, showActiveSession?: boolean): VehicleAssignmentRow => {
  const firstActiveSession = vehicleAssignment.products.find(p => p.activeSessionSince)?.activeSessionSince;
  return {
    rowKey: stringifyDVARowKey({
      vehicleAssignmentId: vehicleAssignment.vehicleAssignmentId,
      pmcIds: vehicleAssignment.products.map(p => p.pmcId),
    }),
    numberPlate: (
      <NumberPlate countryCode={vehicleAssignment.vehicle.countryCode} identifier={vehicleAssignment.vehicle.identifier} />
    ),
    description: <TruncatedText text={vehicleAssignment.vehicle.description} />,
    products: toProductsJsx(toDelegatedParkingProductModel(vehicleAssignment.products)),
    assignedDate: moment(vehicleAssignment.assignedAt).format("DD/MM/YYYY"),
    ...(showActiveSession && {
      activeSessionSince: firstActiveSession ? moment.parseZone(firstActiveSession).format("DD/MM/YYYY HH:mm") : null,
    }),
  };
};

const _createDataMyFleet = (vehicleAssignment: VehicleAssignmentMyFleet, showActiveSession?: boolean): VehicleAssignmentRow => {
  const product = vehicleAssignment.product;

  return {
    rowKey: stringifyDVARowKey({
      vehicleAssignmentId: vehicleAssignment.vehicleAssignmentId,
      pmcIds: product ? [product.pmcId] : [],
    }),
    numberPlate: (
      <NumberPlate countryCode={vehicleAssignment.vehicle.countryCode} identifier={vehicleAssignment.vehicle.identifier} />
    ),
    description: <TruncatedText text={vehicleAssignment.vehicle.description} />,
    products: product ? toProductsJsx(toDelegatedParkingProductModel([product])) : null,
    assignedDate: moment(vehicleAssignment.createdAt).format("DD/MM/YYYY"),
    activationDate: product?.activationDate ? moment.parseZone(product.activationDate).format("DD/MM/YYYY") : null,
    expirationDate: product?.expirationDate ? moment.parseZone(product.expirationDate).format("DD/MM/YYYY") : null,
    ...(showActiveSession && {
      activeSessionSince: product?.activeSessionSince ? moment.parseZone(product.activeSessionSince).format("DD/MM/YYYY HH:mm") : null,
    }),
  };
};

type MenuItemsProps = Readonly<{
  onDelete: () => void;
  onEdit: () => void;
  onRevoke: () => void;
  focusedRow: IDataTableRow | null;
}>;

function MenuItems(props: MenuItemsProps) {
  const { t } = useTranslation(["fleetManagerTable", "myFleet"]);
  const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling);
  const revokeLabel = enableFleetVehicleScheduling ? t("contextMenuActions.revokeProduct") : t("contextMenuActions.revokeVehicle");

  return (
    <>
      <MenuItem key="edit" onClick={() => props.onEdit()}>
        <EditIcon />
        {t("contextMenuActions.editVehicle")}
      </MenuItem>
      <MenuItem 
        key="revoke" 
        disabled={!props.focusedRow?.products}
        onClick={() => props.onRevoke()}>
        <ForbiddenIconMenu />
        {revokeLabel}
      </MenuItem>
      <DeleteDelegeeMenuItem key="delete" onClick={() => props.onDelete()}>
        <DeleteDelegeeIcon />
        {t("contextMenuActions.deleteVehicle")}
      </DeleteDelegeeMenuItem>
    </>
  );
}

function VehiclesTableMyFleet(props: VehicleAssignmentsTableProps) {
  const dispatch = useAppDispatch();
  const vehicleAssignments = useAppSelector((state) => state.parkingProducts.vehicleAssignmentsMyFleet);
  const vehicleAssignmentsOverview = useAppSelector((state) => state.parkingProducts.vehicleAssignmentsMyFleetOverview);
  const parkingProducts = useAppSelector((state) => state.parkingProducts.parkingProducts.data);
  const hasParkingProducts = (parkingProducts?.length ?? 0) > 0;
  const hasUnlimitedContracts = parkingProducts?.find(p => p.hasUnlimitedEntryRights === true) !== undefined;
  const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling) && !hasUnlimitedContracts;
  const showActiveSession = useAppSelector(selectShowActiveSession);

  const getVehicleAssignmentsMyFleet = useCallback(
    (request, excludedIds: string[]) => {
      if(enableFleetVehicleScheduling){
          const query = convertFiltersToGetVehicleAssignmentsMyFleetQuery(request);
        dispatch(parkingProductsThunks.getGetVehicleAssignmentsMyFleetThunk({ query, excludedIds }));
      }else{
        dispatch(parkingProductsActions.getVehicleAssignmentsMyFleet(request, excludedIds) as any);
      }
    },
    [dispatch, enableFleetVehicleScheduling]
  );
  const { t } = useTranslation(["myProducts", "globals", "dataTable", "fleetManagerTable"]);

  const { myFleetContextState, setSorting } = useMyFleetContext();
  const {
    fleetManagerState,
    setSelectedParkingRightIds,
    toggleEditVehicleAssignment,
    toggleDeleteVehicleAssignmentsWarning,
    toggleRevokeVehicleAssignmentsWarning
  } = useFleetManagerContext();

  const dataTableContext = useDataTableContext();

  const { appState } = useAppContext();

  const getFilters = (): DataTableContextFilters[] => [    
    {
      key: FmpFilters.sortBy,
      type: FilterType.QueryParam,
      value: myFleetContextState.sortBy,
    },
    {
      key: FmpFilters.orderDesc,
      type: FilterType.QueryParam,
      value: myFleetContextState.orderDesc?.toString(),
    },
    ...(dataTableContext.state.filters as DataTableContextFilters[]),
  ];

  const getRequestModel = (): WithFiltersAndPaging<ForFleetManager> => {
    const request: WithFiltersAndPaging<ForFleetManager> = {
      entity: {
        seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
        language: appState.selectedLanguage,
      },
      pagination: {
        pageSize: dataTableContext.state.pageSize!,
        pageNumber: dataTableContext.state.pageNumber!,
      },
      filters: getFilters(),
    };
    return request;
  };

  const fetchVehicleAssignments = () => {
    if (hasParkingProducts && appState.user.seasonTicketOwnerCrmId) {
      getVehicleAssignmentsMyFleet(getRequestModel(), props.excludedVehicleAssignmentIds);
    }
  };

  const [filterFetchKey, setFilterFetchKey] = useState(0);

  const memoizedFetch = useCallback(
    (): void => fetchVehicleAssignments(), // eslint-disable-next-line
    [
      dataTableContext.state.pageSize,
      dataTableContext.state.pageNumber,
      myFleetContextState.orderDesc,
      myFleetContextState.sortBy,
      appState.user.seasonTicketOwnerCrmId,
      appState.selectedLanguage,
      hasParkingProducts,
      enableFleetVehicleScheduling,
      props.excludedVehicleAssignmentIds,
      props.fetchKey,
      filterFetchKey
    ]
  );

  
  const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);

  const getMenuItems = () => {
    const menuItems = [
      {
        label: t("fleetManagerTable:assignVehicle"),
        action: props.onAssignVehicleClicked,
        icon: <VehicleIcon/>
      }
    ];

    if (!useBulkVehicleAssignments) {
      menuItems.unshift({
        label: t("fleetManagerTable:inviteFleetMember"),
        action: props.onInviteFleetMemberClicked,
        icon: <PersonIcon/>
      });
    } else {
      menuItems.push({
        label: t("fleetManagerTable:whitelistVehicles"),
        action: props.onWhitelistVehiclesClicked,
        icon: <VehicleIcon/>
      });
    }

    return menuItems;
  };


  useEffect(() => {
    memoizedFetch();
  }, [memoizedFetch]);

  const columns: Column<VehicleAssignmentRow>[] = filteredColumnObjectBuilder.buildVehiclesMyFleetColumns(
    t,
    getParkingProductOptions(parkingProducts ?? [], ParkingProductType.ParkingRights),
    enableFleetVehicleScheduling,
    showActiveSession
  );

  const onSort = (event: React.MouseEvent<unknown>, property: keyof VehicleAssignmentRow) => {
    setSorting(property.toString());
  };

  const getTableRows = (): VehicleAssignmentRow[] => {
    const { pages } = vehicleAssignments;
    if (isNil(pages)) {
      return [];
    }
    const pageData = pages[dataTableContext.state.pageNumber!]?.data;
    if (isNil(pageData)) {
      return [];
    }    
    return pageData.map(va => _createData(va, showActiveSession));
  };

  const getTableRowsOverView = (): VehicleAssignmentRow[] => {
    const { pages } = vehicleAssignmentsOverview;
    if (isNil(pages)) {
      return [];
    }
    const pageData = pages[dataTableContext.state.pageNumber!]?.data;
    if (isNil(pageData)) {
      return [];
    }    
    return pageData.map(va => _createDataMyFleet(va, showActiveSession));
  };

  const isLoading = enableFleetVehicleScheduling ? 
      (vehicleAssignmentsOverview.pages?.[dataTableContext.state.pageNumber!]?.loading ?? false) : 
      (vehicleAssignments.pages?.[dataTableContext.state.pageNumber!]?.loading ?? false) ;
  const rows = enableFleetVehicleScheduling ? getTableRowsOverView() : getTableRows();

  const toAssignedVehicleRow = (row: VehicleAssignmentRow) : AssignedVehicleRow => {
    return {
      assignedDate: row.assignedDate,
      numberPlate: row.numberPlate,
      description: row.description,
      disabled: row.disabled,
      product: null!,
      rowKey: row.rowKey,
      startedDate: null!,
      activationDate: null,
      expirationDate: null,
      remainingFlex: '',
    }
  }

  const handleEdit = (row: VehicleAssignmentRow | null) => {
    if (isNil(row)) {
      return;
    }
    toggleEditVehicleAssignment(toAssignedVehicleRow(row));
  };

  const handleDelete = (row: VehicleAssignmentRow | null) => { 
    if (isNil(row)) {      
      return;
    }    
    toggleDeleteVehicleAssignmentsWarning(toAssignedVehicleRow(row));
  };

  const handleRevoke = (row: VehicleAssignmentRow | null) => { 
    if (isNil(row)) {      
      return;
    }    
    toggleRevokeVehicleAssignmentsWarning(toAssignedVehicleRow(row));
  };

  const parsedSelectedRows = useMemo(() => fleetManagerState.selectedRowKeys.map(parseDVARowKey), [fleetManagerState.selectedRowKeys]);
  const selectedPmcsCount = useMemo(() => {
    return parsedSelectedRows.reduce((acc, x) => acc + x.pmcIds.length, 0)
  }, [parsedSelectedRows]);
  const uniqueVehicleCount = useMemo(() => {
    return new Set(parsedSelectedRows.map((r) => r.vehicleAssignmentId)).size;
  }, [parsedSelectedRows]);

  return (
    <NoPaddingGrid container item direction="row" xs={12}>
      <NoPaddingGrid container item direction="column" xs={12}>
        <DataTable
          rows={rows}
          columns={columns}
          loading={isLoading || !hasParkingProducts}
          orderBy={myFleetContextState.sortBy ?? ""}
          pages={ enableFleetVehicleScheduling ? vehicleAssignmentsOverview.totalPages : vehicleAssignments.totalPages }
          onFilterClicked={() => setFilterFetchKey(prev => prev + 1)}
          onResetFiltersClicked={() => setFilterFetchKey(prev => prev + 1)}
          onRequestSort={onSort}
          translationOverrideKey={"vehicles"}
          selectedRowKeys={fleetManagerState.selectedRowKeys}
          setSelectedRowKeys={setSelectedParkingRightIds}
          renderActionMenu={(focusedRow, onClose) => (
            <MenuItems
              onDelete={() => handleDelete(focusedRow)}
              onEdit={() => handleEdit(focusedRow)}
              onRevoke={() => handleRevoke(focusedRow)}
              focusedRow={focusedRow}
            />
          )}
          order={myFleetContextState.orderDesc ? "desc" : "asc"}
          heading={{
            options: {
              useHeading: true,
              showHeadingCount: false,
            },
            title: t("myFleet:myFleet"),
          }}
          filtering={{
            options: {
              useFilters: true,
            },
          }}
          actions={{
            options: {
              useActions: true,
            },
            revoke: {
              useRevoke: true,
              showRevoke: selectedPmcsCount > 0,
              revokeLabel: `${enableFleetVehicleScheduling ?
                (selectedPmcsCount === 1 ? t("globals:revokeProduct") : t("globals:revokeProducts"))
                : t("globals:revoke")} (${selectedPmcsCount})`,
              onRevokeClicked: props.onRevokeClicked,
            },
            delete: {
              useDelete: true,
              showDelete: enableFleetVehicleScheduling ? uniqueVehicleCount > 0 : fleetManagerState.selectedRowKeys.length > 0,
              deleteLabel: `${enableFleetVehicleScheduling ? (uniqueVehicleCount === 1 ? t("globals:deleteVehicle") : t("globals:deleteVehicles"))
                : t("globals:delete")}
                (${enableFleetVehicleScheduling ? uniqueVehicleCount : fleetManagerState.selectedRowKeys.length})`,
              onDeleteClicked: props.onDeleteClicked,
            },
            menu: {
              useMenu: true,
              showMenu: true,
              menuItems: getMenuItems(),
            }          
          }}
          subHeading={props.subHeading}
        />
      </NoPaddingGrid>
    </NoPaddingGrid>
  );
}

export default VehiclesTableMyFleet;
